import type { TextTransform } from '@/types';
import { FONT_STACK, ensureFontsLoaded, nearestWeight } from '@/lib/fonts';

/**
 * Canvas-backed text measurement for the layout engine.
 *
 * The engine's built-in `estimateWidth` keeps layout runnable without a DOM
 * (which is what makes it unit-testable), but it is a per-character
 * approximation and under-reads heavy uppercase by enough to push a headline
 * past the frame edge. In the browser we can do better: measure with the same
 * font stack the renderer draws with, so what the fitter believes and what the
 * canvas paints are the same thing.
 *
 * Returns width in em units — the width the string would have at font-size 1 —
 * because the engine works in normalized units and does not know pixels.
 */

const PROBE_SIZE = 200;

let context: CanvasRenderingContext2D | null = null;
const cache = new Map<string, number>();

function getContext(): CanvasRenderingContext2D | null {
  if (context) return context;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = 8;
  canvas.height = 8;
  context = canvas.getContext('2d');
  return context;
}

export type CanvasMeasurer = (
  text: string,
  weight: number,
  transform: TextTransform,
) => number;

export function createCanvasMeasurer(): CanvasMeasurer | undefined {
  const ctx = getContext();
  if (!ctx) return undefined;

  return (text, weight, transform) => {
    const snapped = nearestWeight(weight);
    const key = `${snapped}|${transform}|${text}`;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;

    ctx.font = `${snapped} ${PROBE_SIZE}px ${FONT_STACK}`;
    const width = ctx.measureText(text).width / PROBE_SIZE;
    cache.set(key, width);
    return width;
  };
}

/**
 * Fonts must be loaded before measuring, or the first layout is computed
 * against the fallback metrics and then rendered with the real face.
 */
export async function prepareMeasurer(weights: number[]): Promise<CanvasMeasurer | undefined> {
  await ensureFontsLoaded(weights);
  // A face swapping in after load would invalidate everything measured so far.
  cache.clear();
  return createCanvasMeasurer();
}
